import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import { entityService } from "../../services/entityService";

const log = createFileLogger('ENTITY_LINK');

export const entityLinkCommand: SelfContainedCommand = {
  id: 'entity_link',
  description: `Create a typed relationship between two entities.

**Use Cases:**
- Link a Task to a Vehicle (subjectVehicle)
- Link a Document to a Vehicle (hasReceipt)
- Link an Account to a Vehicle (managedBy)
- Link any entities with meaningful relationships

**Relationship Types:**
- subjectVehicle, subjectAccount, subjectEntity - what the entity is about
- managedBy, ownedBy - ownership/management relationships
• hasReceipt, hasDocument, hasTask - what the entity contains/produces
• forVehicle, forAccount - what the entity is for
• producedDocument, createdBySession - creation relationships
• usedAccount, usedCredential - usage relationships`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'fromId',
        type: 'string',
        description: 'Source entity ID',
        required: true
      },
      {
        name: 'toId',
        type: 'string',
        description: 'Target entity ID',
        required: true
      },
      {
        name: 'relationType',
        type: 'string',
        description: 'Type of relationship (e.g., "subjectVehicle", "managedBy", "hasReceipt")',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          fromId: 'task_123', 
          toId: 'vehicle_456', 
          relationType: 'subjectVehicle' 
        },
        expectedOutput: createSuccessResult('Entities linked', {
          fromId: 'task_123',
          toId: 'vehicle_456',
          relationType: 'subjectVehicle'
        }),
        description: 'Link task to vehicle',
        context: 'When creating a task about a vehicle'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 300,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { fromId, toId, relationType } = params;
    
    try {
      log.info('[entity_link] Linking entities:', { fromId, toId, relationType });
      
      if (!fromId || !toId || !relationType) {
        return createErrorResult(
          'Missing required parameters',
          'fromId, toId, and relationType are all required',
          'validation',
          { data: { fromId, toId, relationType } }
        );
      }
      
      await entityService.addLink(fromId, toId, relationType);
      
      log.info('[entity_link] ✅ Entities linked successfully');
      
      return createSuccessResult(
        `Linked ${fromId} to ${toId} via ${relationType}`,
        {
          fromId,
          toId,
          relationType
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityService.addLink',
            affectedEntities: [fromId]
          } as any,
          ui: {
            refreshContext: true
          }
        }
      );
      
    } catch (error: any) {
      log.error('[entity_link] Error:', error);
      return createErrorResult(
        'Entity linking failed',
        `Failed to link entities: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { fromId, toId, relationType },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

